import { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  Link,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";

import { nounsOf, typesOf } from "./Apps.jsx";

/**
 * One app or service, as a product page.
 *
 * ## Who this is for
 *
 * Somebody deciding whether they want the thing, or working out what they
 * already have. Not somebody debugging it. So the order is what it is, what it
 * does for you, what it can touch in plain words, where it runs — and only
 * then the identifiers, versions and exact grants, behind a tab they have to
 * choose.
 *
 * The register screens elsewhere in this console are the other way round on
 * purpose: Apps and Brain (the schema register, `Objects.jsx`) are working
 * surfaces where the precise answer is the answer. This is the page you
 * send somebody.
 *
 * ## Everything on it is from the kernel
 *
 * There is no marketing copy anywhere in this file. The name, the sentence
 * under it, what the app may see and change, its agents and its deployments
 * are all read at render time — so an app whose ceiling is trimmed tomorrow
 * says something different tomorrow, without anybody editing a page.
 *
 * That is also the limit. An app whose manifest nobody wrote a description for
 * has no description here, and the space stays empty rather than being filled
 * with a sentence this file invented about somebody else's software.
 *
 * ## Why "sees" and "changes" rather than the grants
 *
 * `{"type": "Payment", "tags": ["customer"]}` is exact and unreadable. The
 * same helpers the Apps screen uses turn it into *payments* — one vocabulary
 * across both screens, imported rather than rewritten, so the two cannot drift
 * into describing the same ceiling differently.
 */

/** A number and what it counts, for the strip under the hero. */
function Stat({ figure, label, tone }) {
  return (
    <Box sx={{ minWidth: "7rem" }}>
      <Typography
        variant="h2"
        sx={{ fontVariantNumeric: "tabular-nums",
              color: tone ? `kernel.${tone}` : "text.primary" }}
      >
        {figure}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

/** A titled block of prose or content. */
function Section({ title, children, sx }) {
  return (
    <Box sx={{ minWidth: 0, ...sx }}>
      <Typography variant="overline" sx={{ display: "block", color: "text.primary", mb: 1 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

/** One plain sentence about what the app may do, with its own heading. */
function Capability({ heading, children }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, flex: "1 1 14rem", minWidth: 0 }}>
      <Typography variant="overline" sx={{ display: "block", color: "text.secondary" }}>
        {heading}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        {children}
      </Typography>
    </Paper>
  );
}

export default function ProductPage({
  app,
  entry,
  instances = [],
  sites = [],
  deployments = [],
  agents = [],
  collections = {},
  onBack,
}) {
  const [tab, setTab] = useState(0);

  const appType = app?.app_type || app?.app || "";
  const title = entry?.name || app?.label || appType;
  // The catalog's wins where both exist: an app somebody can install is
  // described where it is installed from. Everything else — the kernel, the
  // assistant, the connectors — has no catalog row and says it in its own
  // manifest instead.
  const blurb = entry?.description || app?.description || "";

  // A mountable remote carries its url on the kernel pointer; a standalone
  // app serves a whole site and its pointer deliberately carries none, so the
  // address comes from the control plane. Both are places you can open it.
  const live = [
    ...sites,
    ...instances.filter((i) => i.url).map((i) => ({ url: i.url, name: "" })),
  ];
  const declared = app?.agents || [];
  const running = agents.filter((a) => a.app_type === appType);

  const sees = typesOf(app?.reads, collections);
  const alone = typesOf(app?.service_reads, collections);
  const changes = nounsOf(app?.actions, collections);
  const aloneChanges = nounsOf(app?.service_actions, collections);

  return (
    <Stack spacing={4} sx={{ minWidth: 0 }}>
      <Box>
        <Link
          component="button"
          type="button"
          onClick={onBack}
          variant="body2"
          sx={{ display: "inline-block", mb: 2 }}
        >
          ← Everything installed here
        </Link>

        {/* The hero. One name, one sentence, one thing to do. */}
        <Stack
          direction="row"
          spacing={2}
          sx={{ alignItems: "flex-start", flexWrap: "wrap", mb: 1 }}
        >
          <Typography variant="display" component="h1" sx={{ minWidth: 0, flexGrow: 1 }}>
            {title}
          </Typography>
          {live.length ? (
            <Button
              variant="contained"
              size="large"
              href={live[0].url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open
            </Button>
          ) : null}
        </Stack>

        {blurb ? (
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: "62ch" }}>
            {blurb}
          </Typography>
        ) : null}

        <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap" }}>
          <Chip size="small" variant="outlined" label={appType} />
          {app?.version ? (
            <Chip size="small" variant="outlined" label={`permissions v${app.version}`} />
          ) : null}
          {app?.is_self ? (
            <Chip size="small" variant="outlined" label="this console" />
          ) : null}
        </Stack>
      </Box>

      <Divider />

      <Stack direction="row" spacing={4} sx={{ flexWrap: "wrap", rowGap: 2 }}>
        <Stat figure={live.length} label={live.length === 1 ? "place to open it" : "places to open it"} />
        <Stat
          figure={deployments.length || instances.length}
          label={(deployments.length || instances.length) === 1 ? "deployment" : "deployments"}
        />
        <Stat
          figure={declared.length}
          label={declared.length === 1 ? "AI worker" : "AI workers"}
        />
      </Stack>

      <Section title="What it can touch">
        <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", rowGap: 2 }}>
          <Capability heading="It can see">
            {sees
              ? `Your ${sees}. Never more than you can see yourself.`
              : "No records at all. It holds no grant over anything in this kernel."}
          </Capability>
          <Capability heading="It can change">
            {changes
              ? `Your ${changes}.`
              : "Nothing. It can look and it cannot write."}
          </Capability>
          <Capability heading="On its own">
            {alone || aloneChanges
              ? `With nobody signed in it sees ${alone || "nothing"}${
                  aloneChanges ? ` and can change ${aloneChanges}` : ""
                }.`
              : "Nothing happens unless somebody is signed in and asks for it."}
          </Capability>
        </Stack>
      </Section>

      {declared.length ? (
        <Section title={declared.length === 1 ? "The AI worker it declares" : "The AI workers it declares"}>
          <Stack spacing={1.5}>
            {declared.map((a) => {
              const seen = running.find((r) => r.name === a.name);
              return (
                <Paper key={a.name} variant="outlined" sx={{ p: 2 }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: "baseline", flexWrap: "wrap" }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {a.label || a.name}
                    </Typography>
                    {a.division ? (
                      <Chip size="small" variant="outlined" label={a.division} />
                    ) : null}
                    {seen?.spend?.runs ? (
                      <Typography variant="caption" color="text.secondary">
                        {seen.spend.runs} {seen.spend.runs === 1 ? "run" : "runs"} so far
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        has not run
                      </Typography>
                    )}
                  </Stack>
                  {a.does ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: "62ch" }}>
                      {a.does}
                    </Typography>
                  ) : null}
                </Paper>
              );
            })}
          </Stack>
        </Section>
      ) : null}

      <Section title={live.length ? "Where you can open it" : "Where it runs"}>
        {live.length ? (
          <Stack spacing={1} sx={{ mb: instances.length ? 2 : 0 }}>
            {live.map((l) => (
              <Stack key={l.url} direction="row" spacing={2} sx={{ alignItems: "baseline", flexWrap: "wrap" }}>
                <Link href={l.url} target="_blank" rel="noopener noreferrer" variant="body2">
                  {l.url.replace(/^https?:\/\//, "")}
                </Link>
                {l.name ? (
                  <Typography variant="caption" color="text.secondary">{l.name}</Typography>
                ) : null}
              </Stack>
            ))}
          </Stack>
        ) : null}
        {!live.length && instances.length ? (
          <Stack spacing={1}>
            {instances.map((i) => (
              <Stack
                key={i.client_id}
                direction="row"
                spacing={2}
                sx={{ alignItems: "baseline", flexWrap: "wrap" }}
              >
                {i.url ? (
                  <Link href={i.url} target="_blank" rel="noopener noreferrer" variant="body2">
                    {i.url.replace(/^https?:\/\//, "")}
                  </Link>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Runs inside the platform — there is no page to open.
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  {i.label || (i.by === "lo-provisioner" ? "set up by an install" : "set up with the app itself")}
                  {i.at ? ` on ${String(i.at).slice(0, 10)}` : ""}
                </Typography>
              </Stack>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Nothing is deployed of this yet.
          </Typography>
        )}
      </Section>

      <Divider />

      {/* Everything above is for somebody deciding. Everything below is for
          somebody debugging, which is a different person on a different day —
          so it is a tab they choose rather than a wall they scroll past. */}
      <Box sx={{ minWidth: 0 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="Details" />
          <Tab label="Exact permissions" />
        </Tabs>

        {tab === 0 ? (
          <Stack spacing={1.5}>
            {[
              ["App type", appType],
              ["Permissions version", app?.version ? `v${app.version}` : "none recorded"],
              ["Records it writes into", app?.writes_scope === "instance"
                ? "its own deployment's partition"
                : app?.writes_scope === "org" ? "the organization's" : "it writes none"],
              ["Served from", app?.ui_origin || "nowhere it has declared"],
              ["Identities", instances.map((i) => i.client_id).join(", ") || "none"],
            ].map(([label, value]) => (
              <Stack key={label} direction="row" spacing={2} sx={{ flexWrap: "wrap" }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: "12rem" }}>
                  {label}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: "monospace", overflowWrap: "anywhere", minWidth: 0 }}>
                  {value}
                </Typography>
              </Stack>
            ))}
          </Stack>
        ) : (
          <Stack spacing={2}>
            {[
              ["Reads, with a person signed in", app?.reads],
              ["Reads, with nobody signed in", app?.service_reads],
            ].map(([label, grants]) => (
              <Box key={label}>
                <Typography variant="overline" sx={{ display: "block", color: "text.secondary" }}>
                  {label}
                </Typography>
                {(grants || []).length ? (
                  <Stack component="ul" spacing={0.25} sx={{ m: 0, pl: 2.5 }}>
                    {grants.map((g) => (
                      <Typography component="li" variant="body2" key={g.type} sx={{ fontFamily: "monospace" }}>
                        {g.type}
                        {g.tags?.length ? ` · ${g.tags.join(", ")}` : " · untagged only"}
                      </Typography>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">none</Typography>
                )}
              </Box>
            ))}
            {[
              ["Writes, with a person signed in", app?.actions],
              ["Writes, with nobody signed in", app?.service_actions],
            ].map(([label, verbs]) => (
              <Box key={label}>
                <Typography variant="overline" sx={{ display: "block", color: "text.secondary" }}>
                  {label}
                </Typography>
                {(verbs || []).length ? (
                  <Typography variant="body2" sx={{ fontFamily: "monospace", overflowWrap: "anywhere" }}>
                    {verbs.join(", ")}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">none</Typography>
                )}
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    </Stack>
  );
}
